import { IHttpClient, IAbortController, HttpClientError } from '../../../infra';
import {
    GetSuggestionsRequest,
    GetSuggestionsResponse,
    ListConversationsRequest,
    ListConversationsResponse,
    CreateConversationResponse,
    CreateRunRequest,
    CreateRunResponse,
    ListConversationRunsRequest,
    ListConversationRunsResponse,
    GetRunRequest,
    GetRunResponse,
    SendConversationFeedbackRequest,
    SendRunFeedbackRequest,
    GetConversationRequest,
    GetConversationResponse,
    GenerateConversationTitleRequest,
    GenerateConversationTitleResponse,
    DeleteConversationRequest,
    DeleteConversationResponse,
} from './AssistantDTOs';
import { aRandomString } from '@zdrbm/zdr-native-tools';
import { ExtendedRunStatus } from 'features/pi/model/ConversationRun';
import { PiError } from 'features/pi/model/pi-errors/PiError';

export interface BaseOptions {
    abortController?: IAbortController;
}

export interface IPlacerAssistantClient {
    getSuggestions(
        request: GetSuggestionsRequest,
        options: BaseOptions,
    ): Promise<GetSuggestionsResponse>;
    listConversations(request: ListConversationsRequest): Promise<ListConversationsResponse>;
    createConversation(): Promise<CreateConversationResponse>;
    createRun(request: CreateRunRequest): Promise<CreateRunResponse>;
    listConversationRuns(
        request: ListConversationRunsRequest,
    ): Promise<ListConversationRunsResponse>;
    getRun(request: GetRunRequest): Promise<GetRunResponse>;
    sendConversationFeedback(request: SendConversationFeedbackRequest): Promise<void>;
    sendRunFeedback(request: SendRunFeedbackRequest): Promise<void>;
    getConversation(request: GetConversationRequest): Promise<GetConversationResponse>;
    generateConversationTitle(
        request: GenerateConversationTitleRequest,
    ): Promise<GenerateConversationTitleResponse>;
    deleteConversation(request: DeleteConversationRequest): Promise<DeleteConversationResponse>;
}

export class PlacerAssistantClient implements IPlacerAssistantClient {
    constructor(private client: IHttpClient) {}

    async generateConversationTitle(
        request: GenerateConversationTitleRequest,
    ): Promise<GenerateConversationTitleResponse> {
        const { conversationId } = request;
        try {
            const response = await this.client.post<GenerateConversationTitleResponse>(
                `/v1/conversations/${conversationId}/generate_title`,
                {},
            );
            return response.data;
        } catch (error) {
            throw new PiError('generate conversation title', { cause: error as Error });
        }
    }

    async getConversation(request: GetConversationRequest): Promise<GetConversationResponse> {
        const { conversationId } = request;
        try {
            const response = await this.client.get<GetConversationResponse>(
                `/v1/conversations/${conversationId}`,
            );
            return response.data;
        } catch (error) {
            throw new PiError('get conversation', { cause: error as Error });
        }
    }

    async deleteConversation(request: GetConversationRequest): Promise<DeleteConversationResponse> {
        const { conversationId } = request;
        try {
            const response = await this.client.delete<GetConversationResponse>(
                `/v1/conversations/${conversationId}`,
            );
            return response.data;
        } catch (error) {
            throw new PiError('delete conversation', { cause: error as Error });
        }
    }

    async createConversation(): Promise<CreateConversationResponse> {
        try {
            const response = await this.client.post<CreateConversationResponse>(
                '/v1/conversations',
                {},
            );
            return response.data;
        } catch (error) {
            throw new PiError('create conversation', { cause: error as Error });
        }
    }

    async createRun(request: CreateRunRequest): Promise<CreateRunResponse> {
        const { conversationId, content, screenshotInfo } = request;

        try {
            const result = await this.client.post<CreateRunResponse>(
                `/v1/conversations/${conversationId}/runs`,
                {
                    content,
                    screenshot_info: screenshotInfo,
                },
            );
            return result.data;
        } catch (error) {
            console.error(new PiError('create run', { cause: error as Error }));
            return {
                run: {
                    id: aRandomString(),
                    conversation_id: conversationId,
                    input: content,
                    containers: [],
                    status: ExtendedRunStatus.FAILED,
                    image: undefined,
                    resources: [],
                    started_at: '',
                    created_at: '',
                    updated_at: '',
                },
            };
        }
    }

    async listConversationRuns(
        request: ListConversationRunsRequest,
    ): Promise<ListConversationRunsResponse> {
        const { conversationId } = request;
        try {
            const response = await this.client.get<ListConversationRunsResponse>(
                `/v1/conversations/${conversationId}/runs`,
            );
            return response.data;
        } catch (error) {
            throw new PiError('list conversation runs', { cause: error as Error });
        }
    }

    async getRun(request: GetRunRequest): Promise<GetRunResponse> {
        const { conversationId, runId } = request;
        try {
            const response = await this.client.get<GetRunResponse>(
                `/v1/conversations/${conversationId}/runs/${runId}`,
            );
            return response.data;
        } catch (error) {
            throw new PiError('get run', { cause: error as Error });
        }
    }

    async sendConversationFeedback(request: SendConversationFeedbackRequest): Promise<void> {
        const { conversationId, feedback, score } = request;
        try {
            await this.client.post(`/v1/conversations/${conversationId}/feedback`, {
                feedback,
                score,
            });
        } catch (error) {
            throw new PiError('send conversation feedback', { cause: error as Error });
        }
    }

    async sendRunFeedback(request: SendRunFeedbackRequest): Promise<void> {
        const { conversationId, runId, feedback, score } = request;
        try {
            await this.client.post(
                `/v1/conversations/${conversationId}/feedback`,
                {
                    feedback,
                    score,
                },
                {
                    params: { run_id: runId },
                },
            );
        } catch (error) {
            throw new PiError('send run feedback', { cause: error as Error });
        }
    }

    async listConversations(request: ListConversationsRequest): Promise<ListConversationsResponse> {
        try {
            const response = await this.client.get<ListConversationsResponse>('/v1/conversations', {
                params: request.paging,
            });
            return response.data;
        } catch (error) {
            throw new PiError('list conversations', { cause: error as Error });
        }
    }

    async getSuggestions(
        request: GetSuggestionsRequest,
        options: BaseOptions,
    ): Promise<GetSuggestionsResponse> {
        const { query, maxResults, session } = request;
        const { abortController } = options;

        try {
            const response = await this.client.get<GetSuggestionsResponse>('/v1/suggest', {
                params: {
                    query,
                    max_results: maxResults,
                    session,
                },
                abortController,
            });

            return response.data;
        } catch (error) {
            const httpClientError = error as HttpClientError;
            if (!httpClientError.isAborted) {
                console.error(new PiError('get suggestions', { cause: httpClientError }));
            }

            return {
                suggestions: [],
                session,
            };
        }
    }
}
